import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import Chip from '@material-ui/core/Chip';
import requiredPhoneListValidator from '@utils/validators/requiredPhoneListValidator';
import ActionButton from '@common/buttons/ActionButton/ActionButton';
import DeepTemplateMenu from '@components/Ticketing/Notifications/components/DeepTemplateMenu';
import iconPlus from '@assets/icons/plusGrey.svg';
import sanitizedNumber from '@utils/sanitizedPhoneNumber';

import {
  getEmailGroupVariables,
  getUsersForPhone,
} from '@components/Ticketing/Notifications/TicketSettings/helpers';
import { currentUserDataSelector } from '@store/selectors';
import useAsync from '@services/api/common/useAsync';

import { formatLabelWithPhone } from './helpers';

import useStyles from '../styles';
import AddPhoneModal from './AddPhoneModal';

const initMenuPosition = {
  mouseX: null,
  mouseY: null,
};

const PhoneInput = ({
  disabled,
  form,
  templateVariables,
  name = 'phoneNumber',
}) => {
  const classes = useStyles();
  const tenantCountryCode = useSelector(currentUserDataSelector)
    ?.tenantCountryCode;

  const [menuPosition, setMenuPosition] = useState(initMenuPosition);
  const [userOptions, setUsersOptions] = useState([]);
  const [groupsOptions, setGroupsOptions] = useState([]);
  const [openAddManually, setOpenAddManually] = useState(false);

  // Load user and group options
  useAsync(getUsersForPhone, setUsersOptions);
  useAsync(getEmailGroupVariables, setGroupsOptions);

  const setDefaultMenuPosition = useCallback(
    () => setMenuPosition(initMenuPosition),
    [],
  );

  const options = useMemo(
    () =>
      userOptions.length
        ? userOptions
            .map(option => ({
              ...formatLabelWithPhone(option, tenantCountryCode),
              id:
                option.id ||
                Math.random()
                  .toString(36)
                  .substr(2, 9),
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [tenantCountryCode, userOptions],
  );

  const phoneNumbers = form.getState().values.phoneNumber;

  const handleDelete = useCallback(
    phoneNumber => {
      form.change(
        name,
        phoneNumbers.filter(p => p !== phoneNumber),
      );
    },
    [form, name, phoneNumbers],
  );

  const onAddPhone = useCallback(
    values => {
      const sanitizedValue = sanitizedNumber(
        values.countryCode.split(' ')[1] + values.phone,
      );
      const currentValues = form.getState().values[name];

      if (!currentValues.includes(sanitizedValue)) {
        form.change(name, [...currentValues, sanitizedValue]);
      }
      setOpenAddManually(false);
    },
    [form, name],
  );

  const createPhoneNumberLabel = useCallback(
    phoneNumber => {
      if (options.length === 0) {
        return 'Loading...';
      }
      const option = options.find(el => el.value === phoneNumber);
      const template = templateVariables.find(el => el.value === phoneNumber);
      if (template) {
        return template.label;
      }
      const templateGroup = groupsOptions.find(el => el.value === phoneNumber);
      if (templateGroup) {
        return templateGroup.label;
      }
      return option ? option.label : phoneNumber;
    },
    [options, templateVariables, groupsOptions],
  );

  const handleMenuOpen = useCallback(event => {
    setMenuPosition({
      mouseX: event.clientX + 2,
      mouseY: event.clientY + 4,
    });
  }, []);

  const handleModalClose = useCallback(() => {
    setOpenAddManually(false);
  }, []);

  return (
    <>
      <div className={classes.phoneNumbersWrapper}>
        <div className={classes.phoneNumbersHeader}>
          <ActionButton
            handler={handleMenuOpen}
            icon={<img src={iconPlus} alt="iconPlus" />}
            toolTip="Add template variable"
            disabled={disabled}
          />
          <div className={classes.phoneNumbersContainer}>
            {phoneNumbers.map(phoneNumber => (
              <Chip
                key={phoneNumber}
                variant="outlined"
                label={createPhoneNumberLabel(phoneNumber)}
                size="small"
                className={classes.autocompleteChip}
                onDelete={() => handleDelete(phoneNumber)}
              />
            ))}
          </div>
        </div>
        <div className={classes.errorMessage}>
          {requiredPhoneListValidator(phoneNumbers)}
        </div>
      </div>
      <DeepTemplateMenu
        menuPosition={menuPosition}
        setDefaultMenuPosition={setDefaultMenuPosition}
        form={form}
        name={name}
        templateVariables={templateVariables}
        groupsVariables={groupsOptions}
        usersVariables={options}
        addPhoneNumber={() => setOpenAddManually(true)}
      />
      <AddPhoneModal
        open={openAddManually}
        onClose={handleModalClose}
        onAddPhone={onAddPhone}
      />
    </>
  );
};

PhoneInput.propTypes = {
  disabled: PropTypes.bool,
  form: PropTypes.shape({
    change: PropTypes.func.isRequired,
    getState: PropTypes.func.isRequired,
  }).isRequired,
  templateVariables: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      value: PropTypes.string,
    }),
  ).isRequired,
  name: PropTypes.string,
};

export default PhoneInput;
